const APP_SCROLL_SELECTOR = '#app';

/**
 * 获取应用的滚动容器，默认优先返回 `#app`，未命中时回退到 `window`。
 *
 * @param {string} [selector=APP_SCROLL_SELECTOR] 滚动容器选择器。
 * @returns {HTMLElement|Window|null} 当前滚动容器；服务端环境下返回 `null`。
 */
export function getAppScrollContainer(selector = APP_SCROLL_SELECTOR) {
  if (typeof document === 'undefined') return null;

  return document.querySelector(selector) || window;
}

/**
 * 判断当前滚动容器是否为 `window`。
 *
 * @param {HTMLElement|Window|null} container 待判断的滚动容器。
 * @returns {boolean} 为 `window` 或空值时返回 `true`。
 */
export function isWindowScrollContainer(container) {
  return !container || container === window;
}

/**
 * 获取滚动容器当前的垂直滚动距离。
 *
 * @param {HTMLElement|Window|null} [container=getAppScrollContainer()] 滚动容器。
 * @returns {number} 当前滚动距离。
 */
export function getAppScrollTop(container = getAppScrollContainer()) {
  if (isWindowScrollContainer(container)) {
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  return container.scrollTop || 0;
}

/**
 * 获取滚动容器的可视区域高度。
 *
 * @param {HTMLElement|Window|null} [container=getAppScrollContainer()] 滚动容器。
 * @returns {number} 可视区域高度。
 */
export function getAppClientHeight(container = getAppScrollContainer()) {
  if (isWindowScrollContainer(container)) {
    return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight || 0;
  }

  return container.clientHeight || 0;
}

/**
 * 获取滚动容器内容的总高度。
 *
 * @param {HTMLElement|Window|null} [container=getAppScrollContainer()] 滚动容器。
 * @returns {number} 内容总高度。
 */
export function getAppScrollHeight(container = getAppScrollContainer()) {
  if (isWindowScrollContainer(container)) {
    return Math.max(
      document.documentElement.scrollHeight || 0,
      document.body.scrollHeight || 0
    );
  }

  return container.scrollHeight || 0;
}

/**
 * 设置滚动容器的垂直滚动距离。
 *
 * @param {number} value 目标滚动位置。
 * @param {HTMLElement|Window|null} [container=getAppScrollContainer()] 滚动容器。
 * @returns {void}
 */
export function setAppScrollTop(value, container = getAppScrollContainer()) {
  if (isWindowScrollContainer(container)) {
    window.scrollTo(0, value);
    return;
  }

  container.scrollTop = value;
}
